import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { getUrlParams } from '../../functions/get-url-params';
import { isAuthTokenExpired } from '../../functions/is-auth-token-expired';
import {
  navigateAfterLogin,
  readLoginDeepLinkFromQuery,
  resolveLoginNavigateTarget,
} from '../../functions/login-navigate-to';
import { MessageBus } from '../../services/message/message-bus.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { CheckboxComponent } from '../core/controls/checkbox.component';
import { InputOtpComponent } from '../core/controls/input-otp.component';
import { ApiErrorLabelComponent } from '../core/labels/api-error-label.component';
import { ToastComponent } from '../core/labels/toast.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { LinkLabelComponent } from '../core/labels/link-label.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';

@Component({
  selector: 'app-otp',
  imports: [
    ButtonComponent,
    RouterModule,
    ActionRowComponent,
    ToastComponent,
    CheckboxComponent,
    InputOtpComponent,
    ReactiveFormsModule,
    ApiErrorLabelComponent,
    LinkLabelComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <form
      class="tw-px-4 tw-pt-4 "
      [formGroup]="confirmGroup"
      (ngSubmit)="submitCode()"
    >
      <div class="tw-font-bold tw-py-4">Enter verification code</div>

      @if (error) {
        <div class="tw-flex tw-justify-center tw-pb-8">
          <app-api-error-label [error]="error" />
        </div>
      }

      <app-toast type="Success" icon="fa-light fa-circle-exclamation">
        <div class=" tw-pl-0 tw-pr-4 tw-flex tw-flex-col tw-items-center">
          <div>Your verification code has been sent to:</div>
          <div>{{ this.contact }}</div>
        </div>
      </app-toast>
      <div class="tw-pt-2 tw-pr-5 tw-flex tw-flex-row-reverse">
        <app-link-label (click)="resendCode()">Resend code</app-link-label>
      </div>
      <app-required-fields-note />
      <div class="tw-pb-4 tw-pt-8">
        <div>
          <div class="tw-flex tw-flex-col tw-items-center tw-pb-4">
            <app-input-otp
              [control]="confirmGroup.controls.code"
              [showHelpText]="false"
            />
          </div>
          <small
            >Your verification code is a one-time code. You do not have to
            remember the code.</small
          >
        </div>

        @if (showRememberDevice) {
          <div class="tw-py-4 tw-font-bold">
            <app-checkbox
              [control]="confirmGroup.controls.remember"
              label="Remember this device"
              [showHelpText]="false"
            ></app-checkbox>
          </div>
        }
      </div>

      <div class="tw-flex tw-justify-end tw-pt-4 tw-pr-4 tw-pb-10">
        <app-action-row>
          <app-button
            right1
            label="Submit Code"
            type="submit"
            [disabled]="!confirmGroup.valid"
            [loading]="loading"
          />
          <app-button
            routerLink="/login"
            emphasis="Low"
            right2
            label="Cancel"
          />
        </app-action-row>
      </div>
    </form>
  `,
  styles: ``,
})
export class OtpComponent extends BaseComponent implements OnInit {
  protected messagBus = inject(MessageBus);

  emailSent = false;
  loading = false;
  contact: string;
  token: string;
  expiresAt: string;
  error: any;
  status: string;
  userName: string;
  navigateTo: string;
  recordSource: string;
  recordId: string;
  showRememberDevice = false;
  params: any;

  confirmGroup = new FormGroup({
    code: new FormControl<string>(null, [
      Validators.required,
      Validators.minLength(6),
    ]),
    remember: new FormControl<boolean>(false),
  });

  ngOnInit(): void {
    this.params = getUrlParams();
    this.showRememberDevice = this.params.profile === 'mfa';

    const otp: any = history.state.otp;
    const navigateTo: any = history.state.navigateTo;
    const recordSource: any = history.state.recordSource;
    const recordId: any = history.state.recordId;

    if (!otp) {
      this.router.navigate(['/login']);
      return;
    }
    this.contact = otp.contact;
    this.token = otp.token;
    this.expiresAt = otp.expiresAt;
    this.userName = otp.userName;

    this.navigateTo = navigateTo;
    this.recordSource = recordSource;
    this.recordId = recordId;
  }

  private navigateSessionExpired() {
    if (this.params.profile === 'mfa') {
      this.router.navigate(['/login'], {
        queryParams: { mfaExpired: true },
      });
    } else {
      this.router.navigate(['/login'], {
        queryParams: { passwordResetExpired: true },
      });
    }
  }

  async resendCode() {
    if (isAuthTokenExpired(this.expiresAt)) {
      this.navigateSessionExpired();
      return;
    }

    try {
      const resend = await this.api.auth.resendMfaCode({
        token: this.token,
      });

      if (resend.tokenExpired) {
        this.navigateSessionExpired();
        return;
      }

      this.token = resend.token;
      this.expiresAt = resend.expiresAt;
    } catch (e) {
      this.error = e;
      if (this.error.error.type == 'SecurityTokenExpiredException') {
        this.navigateSessionExpired();
      }
    }
  }

  async submitCode() {
    this.loading = true;
    this.error = null;
    if (!this.confirmGroup.valid) {
      this.confirmGroup.markAllAsTouched();
      this.loading = false;
      return;
    }

    if (isAuthTokenExpired(this.expiresAt)) {
      this.navigateSessionExpired();
      this.loading = false;
      return;
    }

    try {
      const mfaResult = await this.api.auth.mfaVerification({
        code: this.confirmGroup.value.code,
        remember: this.confirmGroup.value.remember,
        token: this.token,
      });
      if (mfaResult.result === 'Success') {
        if (this.params.profile === 'mfa') {
          this.session.clearCache();
          const hexOnly = await this.session.initialize();
          this.session.updateUser.emit(true);
          const deepLink = readLoginDeepLinkFromQuery(this.params);
          const target = resolveLoginNavigateTarget(deepLink);
          if (hexOnly) {
            this.router.navigate([this.session.hexOnlyLandingPath()]);
          } else if (this.navigateTo === 'completedreports') {
            this.router.navigate(['/reports'], {
              queryParams: { tab: 'completed' },
            });
          } else if (this.navigateTo === 'pendingrequests') {
            this.router.navigate(['/reports'], {
              queryParams: { tab: 'pending-reports' },
            });
          } else if (this.navigateTo === 'pendingfilters') {
            this.router.navigate(['/reports'], {
              queryParams: { tab: 'pending-filters' },
            });
          } else if (this.navigateTo === 'completedqueries') {
            this.router.navigate(['/query'], {
              queryParams: { section: 'view-completed' },
            });
          } else if (
            this.navigateTo === 'claim' &&
            this.recordSource &&
            this.recordId
          ) {
            this.router.navigate([
              '/view/claim',
              this.recordSource,
              this.recordId,
            ]);
          } else {
            navigateAfterLogin(this.router, target);
          }
          this.session.resetSessionActivity();
        } else {
          this.router.navigate(['login/password-reset'], {
            state: {
              pwReset: mfaResult,
            },
          });
        }
      } else if (mfaResult.result === 'TokenExpired') {
        this.navigateSessionExpired();
      } else if (mfaResult.result === 'Expired') {
        this.error =
          'The verification code has expired. ' +
          'Use the Resend code link to get another verification code.';
      } else if (mfaResult.result === 'Failed') {
        this.error =
          'The authentication code you entered is not valid. ' +
          'If you feel you have received this message in error re-enter ' +
          'your code or select the Resend code link to receive a new code.';
      }

      this.status = mfaResult.result;
    } catch (e) {
      this.status = 'Failed';
      this.error = e;
      if (this.error.error.type == 'SecurityTokenExpiredException') {
        this.navigateSessionExpired();
      }
    } finally {
      this.loading = false;
    }
  }
}
